import {
    HARD_REJECT_TAGS,
    ALLOWED_TAGS,
    MATHML_TAGS,
    MATHML_ATTRS,
    GLOBAL_ATTRS,
    ATTR_PREFIX_BLOCKLIST,
    ATTR_FULL_BLOCKLIST,
    ATTR_PATTERN_BLOCKLIST,
    ALLOWED_STYLE_PROPS,
    VERTICAL_ALIGN_KEYWORDS,
    TRANSFORM_PROPS,
    MIN_TEXT_CONTRAST,
    TEXT_COLOR_NAMES,
    DEFAULT_IMAGE_MIME_TYPES,
    LINK_ATTRS,
    STYLE_CLASS_TAGS,
    FORMULA_IMG_ATTRS,
    FORMULA_LOADER_CLASS,
    FORMULA_LOADER_SRC,
    BLOCK_TAGS,
    FLOW_CONTAINER_TAGS,
    UNWRAP_BLOCK_TAGS,
    TOOLTIP_CLASS,
    TEXT_DIRECTIONS,
    IMAGE_FIGURE_CLASS,
    TABLE_FIGURE_CLASS,
    FIGURE_CLASS_PATTERN,
    IMAGE_CLASS_PATTERN,
    RESIZED_IMAGE_CLASS,
    RESIZED_WIDTH_PATTERN,
    OWN_CONTENT_URL_ATTRS,
    OWN_CONTENT_URL_SCHEMES,
    OWN_CONTENT_REJECT_TAGS,
    RELAXED_CLASS_PATTERN,
    RELAXED_KEYWORDS,
    RELAXED_LENGTHS,
    LengthLimit,
    BORDER_WIDTH,
    BORDER_STYLES,
    PX_PER_UNIT,
    MIN_BACKGROUND_LUMINANCE,
    BACKGROUND_COLOR_NAMES,
    SanitizeOptions
} from './whitelist';

interface ResolvedOptions {
    allowImageDataUrls: boolean;
    allowedImageMimeTypes: string[];
    allowImg: boolean;
    allowLinks: boolean;
    allowFigures: boolean;
    allowHeadings: boolean;
    allowedClasses: Set<string>;
    relaxed: boolean;
}

function resolveOptions(opts: SanitizeOptions): ResolvedOptions {
    return {
        allowImageDataUrls: opts.allowImageDataUrls ?? true,
        allowedImageMimeTypes: opts.allowedImageMimeTypes ?? DEFAULT_IMAGE_MIME_TYPES,
        allowImg: opts.allowImg ?? true,
        allowLinks: opts.allowLinks ?? false,
        allowFigures: opts.allowFigures ?? false,
        allowHeadings: opts.allowHeadings ?? true,
        allowedClasses: new Set(opts.allowedClasses ?? []),
        relaxed: !opts.ownContent && (opts.relaxed ?? false)
    };
}

function hasKey(record: Record<string, unknown>, key: string): boolean {
    return Object.prototype.hasOwnProperty.call(record, key);
}

/**
 * Sanitize an HTML fragment string against the paste-guard whitelist.
 */
export function sanitize(html: string, options: SanitizeOptions = {}): string {
    const opts = resolveOptions(options);
    const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
    defuseForms(doc.body);
    if (options.ownContent) {
        sanitizeOwnContent(doc.body, opts);
    } else {
        // A relaxed paste goes through the same whitelist and only keeps more
        // formatting (opts.relaxed, see isRelaxedStyle).
        sanitizeChildren(doc.body, opts);
        if (opts.relaxed) keepReadableColors(doc.body, [0, 0, 0], [255, 255, 255]);
    }
    return doc.body.innerHTML;
}

// DOM clobbering: a <form> exposes its controls as properties, so in a
// browser <form><input name="childNodes"> makes form.childNodes the input,
// and a walk over the form skips everything in it. Forms are never kept, so
// each one becomes a <div> before the walk. This uses the accessors of the
// prototypes, which a control cannot override.
function nodeGetter<T>(name: string): (this: Node) => T {
    return Object.getOwnPropertyDescriptor(Node.prototype, name)!.get! as (this: Node) => T;
}

function defuseForms(parent: Node): void {
    const childNodesOf = nodeGetter<NodeListOf<ChildNode>>('childNodes');
    const nodeNameOf = nodeGetter<string>('nodeName');
    const ownerDocumentOf = nodeGetter<Document>('ownerDocument');
    for (const child of Array.from(childNodesOf.call(parent))) {
        defuseForms(child);
        if (nodeNameOf.call(child).toUpperCase() !== 'FORM') continue;
        const div = Document.prototype.createElement.call(ownerDocumentOf.call(child), 'div');
        // Keep style and dir for the normal filter, so a hidden form stays hidden.
        const attributesOf = Object.getOwnPropertyDescriptor(Element.prototype, 'attributes')!.get! as (this: Element) => NamedNodeMap;
        for (const attr of Array.from(attributesOf.call(child as Element))) {
            Element.prototype.setAttribute.call(div, attr.name, attr.value);
        }
        for (const grandchild of Array.from(childNodesOf.call(child))) {
            Node.prototype.appendChild.call(div, grandchild);
        }
        Node.prototype.replaceChild.call(parent, div, child);
    }
}

// Content that an editor of this page put on the clipboard (cut, copy or drag
// inside or between answer boxes) is exactly what that editor already held:
// the paste guard only treats a paste as own content when its HTML matches a
// copy it remembered (handler.ts). Pasting it back adds nothing new, so it is
// not filtered against the whitelist; an incomplete whitelist would silently
// change a candidate's or author's own content when it is moved. As a second
// line of defence, what must never reach the editor is still removed: scripts,
// embedded documents and SVG animation, event handlers, script URLs, and
// AngularJS bindings (directive elements, attributes and classes, and {{ }}
// interpolation in attribute values).
function sanitizeOwnContent(parent: Element, opts: ResolvedOptions): void {
    for (const node of Array.from(parent.childNodes)) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        const el = node as Element;
        const tag = el.tagName.toLowerCase();
        if (HARD_REJECT_TAGS.has(tag) || OWN_CONTENT_REJECT_TAGS.has(tag) || isAngularName(tag)) {
            el.remove();
            continue;
        }
        for (const name of Array.from(el.attributes, a => a.name)) {
            if (!isOwnContentAttrSafe(el, name, opts)) {
                el.removeAttribute(name);
            }
        }
        if (el.hasAttribute('class')) {
            keepClassTokens(el, classTokens(el).filter(name => !isClassDirective(name)));
        }
        if (el.hasAttribute('style')) {
            removeScriptStyles(el);
        }
        sanitizeOwnContent(el, opts);
    }
}

function isOwnContentAttrSafe(el: Element, name: string, opts: ResolvedOptions): boolean {
    const lower = name.toLowerCase();
    if (isBlockedAttrName(name) || isAngularName(lower)) return false;
    const value = el.getAttribute(name) ?? '';
    if (value.includes('{{')) return false;
    if (!OWN_CONTENT_URL_ATTRS.has(lower)) return true;
    const urls = lower === 'srcset' ? srcsetUrls(value) : [value];
    const asImage = isImageUrlAttr(el.tagName.toLowerCase(), lower);
    return urls.every(url => isOwnContentUrl(url, asImage, opts));
}

// Own content keeps every URL that cannot run code, also protocol-relative
// URLs and tel: links that a foreign paste loses. A data: URL stays only where
// the browser shows it as an image; as a link or form target, an SVG image
// is a document that can run script.
function isOwnContentUrl(url: string, asImage: boolean, opts: ResolvedOptions): boolean {
    const normalized = normalizeUrl(url);
    const scheme = normalized.match(/^([a-z][a-z0-9+.-]*):/i);
    if (!scheme) return true;
    const name = scheme[1].toLowerCase();
    if (name === 'data') return asImage && isUrlAllowed(normalized, opts);
    return OWN_CONTENT_URL_SCHEMES.has(name);
}

function isImageUrlAttr(tag: string, attr: string): boolean {
    return (tag === 'img' && (attr === 'src' || attr === 'srcset'))
        || (tag === 'source' && attr === 'srcset')
        || (tag === 'video' && attr === 'poster')
        || ((tag === 'image' || tag === 'feimage') && (attr === 'href' || attr === 'xlink:href'))
        || attr === 'background';
}

// The attribute names that no paste keeps, own content included.
function isBlockedAttrName(name: string): boolean {
    const lower = name.toLowerCase();
    return ATTR_FULL_BLOCKLIST.has(lower) || ATTR_PATTERN_BLOCKLIST.test(name)
        || ATTR_PREFIX_BLOCKLIST.some(prefix => lower.startsWith(prefix));
}

// The URLs of a srcset, as the HTML parser reads it: a URL runs to the next
// whitespace (a comma inside it, as in a data: URL, belongs to it), and its
// descriptors run to the next comma.
function srcsetUrls(srcset: string): string[] {
    const urls: string[] = [];
    let i = 0;
    while (i < srcset.length) {
        while (i < srcset.length && /[\s,]/.test(srcset[i])) i++;
        const start = i;
        while (i < srcset.length && !/\s/.test(srcset[i])) i++;
        let url = srcset.slice(start, i);
        if (url.endsWith(',')) {
            url = url.replace(/,+$/, '');
        } else {
            while (i < srcset.length && srcset[i] !== ',') i++;
        }
        if (url) urls.push(url);
    }
    return urls;
}

// AngularJS normalises a name before it matches it against its directives
// (directiveNormalize): it strips an "x" or "data" prefix with ':', '-' or
// '_', then turns every run of these separators plus the next letter into
// that letter in upper case, except at the start. So "data:ng_click",
// "_ng-init" and "data--ng-init" are ngClick and ngInit. Element and attribute
// names are lower-cased first; class names are not, so the class "ngInclude"
// is a directive too. Built-in directives all start with "ng".
function isAngularName(name: string): boolean {
    const normalized = name
        .replace(/^(x|data)[:\-_]/i, '')
        .replace(/[:\-_]+(.)/g, (_match, letter: string, offset: number) => offset ? letter.toUpperCase() : letter);
    return /^ng[A-Z]/.test(normalized);
}

// AngularJS reads "name" and "name: expression;" in a class attribute as a
// directive. Editor classes never contain ':' or ';' or start with "ng".
function isClassDirective(name: string): boolean {
    // AngularJS reads every run of word characters and '-' in a class value
    // as a possible directive name, so "/ngInclude" holds ngInclude.
    return /[:;]/.test(name) || (name.match(/[\w-]+/g) ?? []).some(isAngularName);
}

const SCRIPT_STYLE = /url\(|expression\(|javascript:|behavior\s*:|-moz-binding/i;

// CSS reads escapes and ignores comments, so "u\72l(" is url( too. The text
// is also tested without any whitespace, which can only remove more.
function isScriptStyle(css: string): boolean {
    // Comments are removed both as CSS does (strings kept, so a quoted "/*"
    // opens no comment) and naively; a match in either removes the style.
    const variants = [cssUnescape(stripCssComments(css)), cssUnescape(css.replace(/\/\*[\s\S]*?\*\//g, ''))];
    return SCRIPT_STYLE.test(css) || variants.some(text => SCRIPT_STYLE.test(text) || SCRIPT_STYLE.test(text.replace(/\s+/g, '')));
}

// CSS first turns \r\n, \r and \f into \n; a hex escape then takes one
// whitespace after it.
function cssUnescape(css: string): string {
    return css
        .replace(/\r\n|[\r\f]/g, '\n')
        .replace(/\\([0-9a-f]{1,6})[ \t\n]?/gi, (_match, hex: string) => {
            const code = parseInt(hex, 16);
            return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : '\ufffd';
        })
        .replace(/\\(.)/g, '$1');
}

// Leaves the style attribute as it is unless a declaration has to go.
function removeScriptStyles(el: Element): void {
    const style = el.getAttribute('style') ?? '';
    if (!isScriptStyle(style)) return;
    const kept = splitDeclarations(style)
        .map(decl => decl.trim())
        .filter(decl => !isScriptStyle(decl));
    // Test the result again: whatever the split missed must not survive.
    setStyle(el, isScriptStyle(kept.join('; ')) ? [] : kept);
}

function keepClassTokens(el: Element, kept: string[]): void {
    setListAttribute(el, 'class', kept, ' ');
}

function sanitizeChildren(parent: Element, opts: ResolvedOptions): void {
    const children = Array.from(parent.childNodes);
    for (const node of children) {
        if (node.nodeType !== Node.ELEMENT_NODE) continue;
        let el = node as Element;
        let tag = el.tagName.toLowerCase();

        if (HARD_REJECT_TAGS.has(tag)) {
            el.remove();
            continue;
        }

        // A b or strong with a normal weight keeps its other formatting as a span.
        if (isNotBold(el, tag) && !hasBlockContent(el)) {
            el = renameElement(el, 'span');
            tag = 'span';
        }

        if (!opts.allowHeadings && HEADING_TAGS.has(tag)) {
            el = headingToParagraph(el);
            tag = 'p';
        }

        const hidden = tag === 'math' ? null : hiddenBy(el);
        if (hidden) {
            // Hidden MathML stays; under visibility:hidden, so do descendants
            // that make themselves visible again.
            const keep = (node: Element) => node.tagName.toLowerCase() === 'math'
                || (hidden === 'visibility' && restoresVisibility(node));
            if (!Array.from(el.querySelectorAll('*')).some(keep)) {
                el.remove();
                continue;
            }
            keepOnly(el, keep);
        }

        if (tag === 'img' && !opts.allowImg && !isFormulaImage(el, opts)) {
            el.remove();
            continue;
        }

        const isLink = tag === 'a' && opts.allowLinks;
        const isMathML = MATHML_TAGS.has(tag);
        // Decided before the children change: figcaption checks its parent figure.
        const isFigure = keepsFigure(el, opts) || keepsCaption(el, opts);
        const isAllowed = (hasKey(ALLOWED_TAGS, tag) || isMathML || isLink || isFigure) && !isNotBold(el, tag);

        sanitizeChildren(el, opts);

        if (!isAllowed || (isLink && !isLinkHrefAllowed(el.getAttribute('href')))) {
            if (UNWRAP_BLOCK_TAGS.has(tag)) {
                unwrapBlock(el, opts);
            } else {
                unwrap(el);
            }
            continue;
        }

        filterAttributes(el, tag, isMathML, opts);

        if (tag === 'img') {
            validateImgSrc(el, opts);
        }
    }
}

// An element that its inline style hides (see TRANSFORM_PROPS in
// whitelist.ts). What the reader could not see is not pasted. Hidden MathML
// stays, because it can be the only copy of a formula (MathJax keeps an
// assistive MathML copy next to the rendered SVG, which a paste cannot keep).
// Everything else in a hidden element goes, because the style that hid it is
// removed (see keepOnly). Returns how the element is hidden: the whole
// subtree, or by visibility, which a descendant can set back to visible.
function hiddenBy(el: Element): 'subtree' | 'visibility' | null {
    const style = el.getAttribute('style');
    if (!style) return null;
    if (isHidden(el, style)) return 'subtree';
    const visibility = styleDeclarations(style).get('visibility');
    return visibility === 'hidden' || visibility === 'collapse' ? 'visibility' : null;
}

function restoresVisibility(el: Element): boolean {
    return styleDeclarations(el.getAttribute('style') ?? '').get('visibility') === 'visible';
}

// Hidden by a rule that hides the whole subtree (all but visibility).
function isHidden(el: Element, style: string): boolean {
    const decls = styleDeclarations(style);
    const value = (name: string) => decls.get(name) ?? '';
    // 0 or 1px; the browser ignores a unitless size other than 0.
    const tiny = (size: string) => /^(0+(\.0*)?(px)?|1(\.0*)?px)$/.test(size);
    if (value('display') === 'none' || (decls.has('opacity') && isZeroValue(value('opacity')))) {
        return true;
    }
    // clip only applies to absolutely positioned elements; transform, scale,
    // width, height and overflow do not apply to plain inline elements, which
    // stay visible with them. Text whose hiding is not certain is kept.
    const tag = el.tagName.toLowerCase();
    return (/^(absolute|fixed)$/.test(value('position')) && isZeroAreaClip(value('clip')))
        || (isBoxed(tag, value) && (
            // A var() fallback may never apply, so it proves nothing.
            TRANSFORM_PROPS.some(name => !value(name).includes('var(') && scalesToZero(value(name)))
            || (!value('scale').includes('var(') && value('scale').split(/\s+/).slice(0, 2).some(isZeroValue))
            // A replaced element such as an image shrinks with its box; other
            // content overflows a tiny box unless the overflow is clipped.
            || (tiny(value('width')) && tiny(value('height'))
                && (REPLACED_TAGS.has(tag) || /^(hidden|clip)$/.test(value('overflow'))))));
}

// Elements that have their own box by default, besides block elements.
const BOXED_TAGS = new Set([
    'img', 'svg', 'video', 'canvas', 'iframe', 'object', 'embed', 'input', 'button', 'select', 'textarea',
    'table', 'caption', 'colgroup', 'col', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th'
]);
const REPLACED_TAGS = new Set(['img', 'svg', 'video', 'canvas', 'iframe', 'object', 'embed', 'input']);

// The element has its own box: it is positioned, floats, has a display other
// than inline, or is an image or a block element by default.
function isBoxed(tag: string, value: (name: string) => string): boolean {
    if (/^(absolute|fixed)$/.test(value('position')) || /^(left|right)$/.test(value('float'))) return true;
    const display = value('display');
    // An inline image or other replaced element still has a box.
    if (display) return display !== 'contents' && (display !== 'inline' || REPLACED_TAGS.has(tag));
    return BOXED_TAGS.has(tag) || BLOCK_TAGS.has(tag) || UNWRAP_BLOCK_TAGS.has(tag);
}

// The declarations of an inline style by lower-case property name, without
// !important and in lower case. An !important declaration wins over a normal
// one; otherwise a later declaration of the same property wins.
function styleDeclarations(style: string): Map<string, string> {
    const decls = new Map<string, Declaration>();
    for (const decl of parseDeclarations(style)) {
        if (decl.important || !decls.get(decl.prop)?.important) decls.set(decl.prop, decl);
    }
    return new Map(Array.from(decls, ([prop, decl]) => [prop, decl.value.toLowerCase()]));
}

interface Declaration {
    prop: string;      // lower case
    value: string;     // as written, without !important
    important: boolean;
    written: string;   // the value as written, with !important
}

function parseDeclarations(style: string): Declaration[] {
    const decls: Declaration[] = [];
    for (const declaration of splitDeclarations(style)) {
        // CSS ignores comments, so "display/**/:none" is display: none.
        const text = stripCssComments(declaration);
        const colon = text.indexOf(':');
        if (colon < 0) continue;
        const prop = text.slice(0, colon).trim().toLowerCase();
        const raw = text.slice(colon + 1);
        const important = /!\s*important\s*$/i.test(raw);
        const value = raw.replace(/!\s*important\s*$/i, '').trim();
        if (prop) decls.push({ prop, value, important, written: raw.trim() });
    }
    return decls;
}

// Splits a style at the semicolons that end a declaration, as CSS does: a
// semicolon in a string, in parentheses, in a comment or after a backslash
// does not end one, so url("data:image/png;base64,...") stays whole.
function splitDeclarations(style: string): string[] {
    const parts: string[] = [];
    let start = 0;
    let depth = 0;
    let quote = '';
    for (let i = 0; i < style.length; i++) {
        const ch = style[i];
        if (ch === '\\') {
            i++;
        } else if (quote) {
            if (ch === quote) quote = '';
        } else if (ch === '/' && style[i + 1] === '*') {
            const end = style.indexOf('*/', i + 2);
            i = end < 0 ? style.length : end + 1;
        } else if (ch === '"' || ch === "'") {
            quote = ch;
        } else if (ch === '(') {
            depth++;
        } else if (ch === ')') {
            depth = Math.max(0, depth - 1);
        } else if (ch === ';' && depth === 0) {
            parts.push(style.slice(start, i));
            start = i + 1;
        }
    }
    parts.push(style.slice(start));
    return parts.filter(part => part.trim());
}

// Removes CSS comments outside strings, as the CSS parser does.
function stripCssComments(css: string): string {
    let result = '';
    let quote = '';
    for (let i = 0; i < css.length; i++) {
        const ch = css[i];
        if (ch === '\\' && i + 1 < css.length) {
            result += ch + css[++i];
        } else if (quote) {
            result += ch;
            if (ch === quote) quote = '';
        } else if (ch === '"' || ch === "'") {
            quote = ch;
            result += ch;
        } else if (ch === '/' && css[i + 1] === '*') {
            const end = css.indexOf('*/', i + 2);
            i = end < 0 ? css.length : end + 1;
        } else {
            result += ch;
        }
    }
    return result;
}

// Sets the style to these declarations, or removes it when none is left.
function setStyle(el: Element, decls: string[]): void {
    setListAttribute(el, 'style', decls, '; ');
}

// Sets an attribute to a list of items, or removes it when the list is empty.
function setListAttribute(el: Element, name: string, items: string[], separator: string): void {
    if (items.length === 0) {
        el.removeAttribute(name);
    } else {
        el.setAttribute(name, items.join(separator));
    }
}

function isZeroValue(value: string): boolean {
    return /^[+-]?(0+(\.0*)?|\.0+)%?$/.test(value);
}

// scale(), scaleX(), scaleY() or scale3d() with a zero x or y factor.
function scalesToZero(transform: string): boolean {
    const scale = /scale(3d|x|y)?\(([^)]*)\)/g;
    let match: RegExpExecArray | null;
    while ((match = scale.exec(transform)) !== null) {
        const args = match[2].split(/[\s,]+/).filter(arg => arg);
        const kind = match[1] ?? '';
        const factors = kind === 'x' || kind === 'y' ? args.slice(0, 1) : args.slice(0, 2);
        if (factors.some(isZeroValue)) return true;
    }
    return false;
}

// clip:rect(top, right, bottom, left) with no height or no width.
function isZeroAreaClip(clip: string): boolean {
    const match = clip.match(/^rect\(([^)]*)\)$/);
    if (!match) return false;
    const [top, right, bottom, left] = match[1].split(/[\s,]+/).filter(arg => arg);
    if (left === undefined) return false;
    return (top === bottom && top !== 'auto') || (left === right && left !== 'auto');
}

// Removes everything under parent except the elements that keep() accepts
// and the path to them.
function keepOnly(parent: Element, keep: (el: Element) => boolean): void {
    for (const node of Array.from(parent.childNodes)) {
        const child = node.nodeType === Node.ELEMENT_NODE ? node as Element : null;
        if (child && keep(child)) continue;
        if (child && Array.from(child.querySelectorAll('*')).some(keep)) {
            keepOnly(child, keep);
        } else {
            node.remove();
        }
    }
}

const BLOCK_SELECTOR = Array.from(new Set([...BLOCK_TAGS, ...UNWRAP_BLOCK_TAGS])).join(',');

function hasBlockContent(el: Element): boolean {
    return el.querySelector(BLOCK_SELECTOR) != null;
}

const HEADING_TAGS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']);

// A heading becomes a bold paragraph. In the candidate app and in the
// response PDF, an <h2> looks like a question header (QuestionHeadline.html),
// and candidates cannot create headings themselves.
function headingToParagraph(el: Element): Element {
    const p = renameElement(el, 'p');
    const strong = p.ownerDocument.createElement('strong');
    moveChildren(p, strong);
    p.appendChild(strong);
    return p;
}

// The same element with another tag name, its attributes and children.
function renameElement(el: Element, tag: string): Element {
    const renamed = el.ownerDocument.createElement(tag);
    for (const attr of Array.from(el.attributes)) {
        renamed.setAttribute(attr.name, attr.value);
    }
    moveChildren(el, renamed);
    el.replaceWith(renamed);
    return renamed;
}

function moveChildren(from: Element, to: Element): void {
    while (from.firstChild) to.appendChild(from.firstChild);
}

// Google Docs wraps a copied selection in <b style="font-weight:normal" id=
// "docs-internal-guid-...">. CKEditor turns every <b> into bold, so the whole
// paste became bold. A b or strong that sets a normal weight becomes a span
// that keeps its other formatting, or is unwrapped when it holds blocks, as
// the Google Docs wrapper does.
function isNotBold(el: Element, tag: string): boolean {
    if (tag !== 'b' && tag !== 'strong') return false;
    // The declaration that takes effect, as the browser picks it.
    const weight = styleDeclarations(el.getAttribute('style') ?? '').get('font-weight');
    if (!weight) return false;
    return weight === 'normal' || weight === 'lighter' || parseFloat(weight) < 600;
}

function isFormulaImage(el: Element, opts: ResolvedOptions): boolean {
    const src = el.getAttribute('src');
    if (!FORMULA_IMG_ATTRS.some(name => el.hasAttribute(name)) || src == null) return false;
    if (src.trim().toLowerCase().startsWith('data:')) return isUrlAllowed(src, opts);
    return isFormulaLoader(el, src);
}

// A formula that has not been rendered yet, see FORMULA_LOADER_SRC.
function isFormulaLoader(el: Element, src: string): boolean {
    return classTokens(el).includes(FORMULA_LOADER_CLASS) && FORMULA_LOADER_SRC.test(normalizeUrl(src));
}

// Mirror the browser URL parser before inspecting a scheme: it strips leading
// and trailing C0 controls/spaces, drops tab and newline anywhere and treats
// backslashes as slashes ("java\tscript:" is javascript:, "/\t/" is "//").
function normalizeUrl(url: string): string {
    return url
        .replace(/^[\u0000- ]+|[\u0000- ]+$/g, '')
        .replace(/[\t\n\r]/g, '')
        .replace(/\\/g, '/');
}

function isLinkHrefAllowed(href: string | null): boolean {
    if (href == null) return false;
    const trimmed = normalizeUrl(href);
    if (trimmed.startsWith('//')) return false;
    // Relative URL or fragment — anything that doesn't have a scheme.
    if (!/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return true;
    return /^(https?|mailto):/i.test(trimmed);
}

function filterAttributes(el: Element, tag: string, isMathML: boolean, opts: ResolvedOptions): void {
    let classHandled = false;
    if (tag === 'span') {
        const isTooltip = classTokens(el).includes(TOOLTIP_CLASS);
        if (!isTooltip) {
            el.removeAttribute('data');
        }
        filterStyleClasses(el, opts, isTooltip ? TOOLTIP_CLASS : null);
        classHandled = true;
    } else if (STYLE_CLASS_TAGS.has(tag)) {
        filterStyleClasses(el, opts);
        classHandled = true;
    } else if (tag === 'figure') {
        filterClassTokens(el, FIGURE_CLASS_PATTERN);
        classHandled = true;
    } else if (tag === 'img') {
        filterClassTokens(el, IMAGE_CLASS_PATTERN);
        classHandled = true;
    } else if (opts.relaxed && (tag === 'mark' || tag === 'code')) {
        filterClassTokens(el, RELAXED_CLASS_PATTERN);
        classHandled = true;
    }

    const perTagAttrs = isMathML ? null : (tag === 'a' ? LINK_ATTRS : (ALLOWED_TAGS[tag] ?? []));
    const attrNames = Array.from(el.attributes, a => a.name);
    for (const name of attrNames) {
        if (classHandled && name.toLowerCase() === 'class') continue;
        if (!isAttrAllowed(name, perTagAttrs, isMathML)) {
            el.removeAttribute(name);
        }
    }

    if (el.hasAttribute('style')) {
        filterStyle(el, isResizedImage(el, tag), opts.relaxed);
    }
}

// A block image figure or an inline image with the ImageResize class.
function isResizedImage(el: Element, tag: string): boolean {
    const classes = classTokens(el);
    if (!classes.includes(RESIZED_IMAGE_CLASS)) return false;
    return tag === 'img' || (tag === 'figure' && classes.includes(IMAGE_FIGURE_CLASS));
}

function classTokens(el: Element): string[] {
    return (el.getAttribute('class') ?? '').split(/\s+/).filter(name => name);
}

function filterStyleClasses(el: Element, opts: ResolvedOptions, extraClass: string | null = null): void {
    keepClassTokens(el, classTokens(el).filter(name => name === extraClass || opts.allowedClasses.has(name)
        || (opts.relaxed && RELAXED_CLASS_PATTERN.test(name))));
}

function filterClassTokens(el: Element, pattern: RegExp): void {
    keepClassTokens(el, classTokens(el).filter(name => pattern.test(name)));
}

// A figure that CKEditor writes for a block image or a table, see allowFigures.
function keepsFigure(el: Element, opts: ResolvedOptions): boolean {
    if (!opts.allowFigures || el.tagName.toLowerCase() !== 'figure') return false;
    const classes = classTokens(el);
    const childTags = Array.from(el.children, child => child.tagName.toLowerCase());
    if (classes.includes(TABLE_FIGURE_CLASS)) return childTags.includes('table');
    if (classes.includes(IMAGE_FIGURE_CLASS)) return opts.allowImg && childTags.includes('img');
    return false;
}

function keepsCaption(el: Element, opts: ResolvedOptions): boolean {
    return el.tagName.toLowerCase() === 'figcaption' && el.parentElement != null && keepsFigure(el.parentElement, opts);
}

function filterStyle(el: Element, allowResizedWidth: boolean, relaxed: boolean): void {
    const cleaned: string[] = [];
    // The value as written: an !important value fails the value checks.
    for (const { prop, written: value, value: plain } of parseDeclarations(el.getAttribute('style') ?? '')) {
        if (prop === 'width' && allowResizedWidth && isResizedWidth(value)) {
            cleaned.push(`${prop}: ${value}`);
            continue;
        }
        if (/url\(|expression\(|javascript:|\\/i.test(value)) continue;
        if (relaxed && isRelaxedStyle(prop, value)) {
            // Without !important: a paste must not override the app's rules,
            // such as the dyslexia font that invigilators switch on.
            cleaned.push(`${prop}: ${plain}`);
            continue;
        }
        if (!ALLOWED_STYLE_PROPS.has(prop)) continue;
        if (prop === 'vertical-align' && !VERTICAL_ALIGN_KEYWORDS.has(value.toLowerCase())) continue;
        if (prop === 'color' && !isReadableColor(value)) continue;
        cleaned.push(`${prop}: ${value}`);
    }
    setStyle(el, cleaned);
}

// A foreign text colour is kept only when it is readable in the white answer
// box. Background colours are removed on paste, so white or pale text would be
// invisible (IBPIT-1951). A value that is not a plain hex, rgb(), hsl() or
// TEXT_COLOR_NAMES colour is dropped too, and the text gets the default colour.
// Formatting that a relaxed paste keeps on top of ALLOWED_STYLE_PROPS.
function isRelaxedStyle(prop: string, written: string): boolean {
    const value = written.replace(/\s*!\s*important\s*$/i, '').trim().toLowerCase();
    if (hasKey(RELAXED_KEYWORDS, prop)) return RELAXED_KEYWORDS[prop].test(value);
    if (prop === 'background-color') return isLightBackground(value);
    if (/^border(-(top|right|bottom|left))?$/.test(prop)) return isBorder(value);
    if (!hasKey(RELAXED_LENGTHS, prop)) return false;
    const limit = RELAXED_LENGTHS[prop];
    if (prop === 'font-size') return FONT_SIZE_KEYWORDS.has(value) || isLengthWithin(value, limit, PX_PER_UNIT.em);
    // Other lengths in em, ex or ch grow with the font, which a paste can set
    // up to the largest kept size; check them at that size.
    const em = RELAXED_LENGTHS['font-size'].max;
    if (prop === 'line-height') return value === 'normal' || isUnitlessLineHeight(value) || isLengthWithin(value, limit, em);
    if (prop === 'letter-spacing' || prop === 'word-spacing') return value === 'normal' || isLengthWithin(value, limit, em);
    if (prop === 'text-indent') return isLengthWithin(value, limit, em);
    // margin and padding: one to four sides
    const sides = value.split(/\s+/);
    return sides.length <= 4 && sides.every(side => (side === 'auto' && prop.startsWith('margin')) || isLengthWithin(side, limit, em));
}

const FONT_SIZE_KEYWORDS = new Set(['xx-small', 'x-small', 'small', 'medium', 'large', 'x-large', 'xx-large', 'smaller', 'larger']);

function isUnitlessLineHeight(value: string): boolean {
    return /^\d{1,2}(\.\d+)?$/.test(value) && parseFloat(value) >= 0.8 && parseFloat(value) <= 5;
}

// A length within the limit, after converting it to CSS pixels with emPx
// pixels per em (ex and ch count as half an em). Only 0 may go without a unit.
function isLengthWithin(value: string, limit: LengthLimit, emPx: number): boolean {
    const match = value.match(/^(\d+(\.\d+)?|\.\d+)(px|pt|pc|in|cm|mm|em|rem|ex|ch|%)?$/);
    if (!match) return false;
    const amount = parseFloat(match[1]);
    const unit = match[3];
    let px: number;
    if (!unit) {
        if (amount !== 0) return false;
        px = 0;
    } else if (unit === '%') {
        if (!limit.percent) return false;
        px = amount * limit.percent;
    } else if (unit === 'em') {
        px = amount * emPx;
    } else if (unit === 'ex' || unit === 'ch') {
        px = amount * emPx / 2;
    } else {
        px = amount * PX_PER_UNIT[unit];
    }
    return px >= limit.min && px <= limit.max;
}

// border: up to three parts, each a width, a style or a colour.
function isBorder(value: string): boolean {
    const parts = value.match(/(rgba?|hsla?)\([^()]*\)|[^\s]+/g) ?? [];
    return parts.length >= 1 && parts.length <= 3 && parts.every(part =>
        /^(thin|medium|thick)$/.test(part) || BORDER_STYLES.has(part)
        || isLengthWithin(part, BORDER_WIDTH, RELAXED_LENGTHS['font-size'].max) || parseColor(part, BACKGROUND_COLOR_NAMES) != null);
}

// A background on which dark text stays readable (see MIN_BACKGROUND_LUMINANCE).
function isLightBackground(value: string): boolean {
    const rgba = parseColor(value, BACKGROUND_COLOR_NAMES);
    return rgba != null && luminance(...blend(rgba, [255, 255, 255])) >= MIN_BACKGROUND_LUMINANCE;
}

type Rgb = [number, number, number];

// What the reader sees: a colour with its alpha composited over a base colour.
function blend(rgba: [number, number, number, number], base: Rgb): Rgb {
    const alpha = rgba[3];
    return [0, 1, 2].map(i => alpha * rgba[i] + (1 - alpha) * base[i]) as Rgb;
}

function contrast(a: Rgb, b: Rgb): number {
    const [lighter, darker] = [luminance(...a), luminance(...b)].sort((x, y) => y - x);
    return (lighter + 0.05) / (darker + 0.05);
}

// A relaxed paste keeps text colours that are readable on white and light
// backgrounds, but a pair of them can still be unreadable, for example grey
// text on a grey background. Walk the tree with the colours the reader sees;
// where text and background do not reach MIN_TEXT_CONTRAST, drop the
// element's background first, then its text colour.
function keepReadableColors(parent: Element, color: Rgb, background: Rgb): void {
    for (const el of Array.from(parent.children)) {
        const decls = styleDeclarations(el.getAttribute('style') ?? '');
        const ownBackground = decls.has('background-color') ? parseColor(decls.get('background-color')!, BACKGROUND_COLOR_NAMES) : null;
        const ownColor = decls.has('color') ? parseColor(decls.get('color')!) : null;
        let bg = ownBackground ? blend(ownBackground, background) : background;
        let fg = ownColor ? blend(ownColor, bg) : color;
        if (contrast(fg, bg) < MIN_TEXT_CONTRAST && ownBackground) {
            removeDeclaration(el, 'background-color');
            bg = background;
            fg = ownColor ? blend(ownColor, bg) : color;
        }
        if (contrast(fg, bg) < MIN_TEXT_CONTRAST && ownColor) {
            removeDeclaration(el, 'color');
            fg = color;
        }
        keepReadableColors(el, fg, bg);
    }
}

function removeDeclaration(el: Element, prop: string): void {
    setStyle(el, parseDeclarations(el.getAttribute('style') ?? '').filter(d => d.prop !== prop).map(d => `${d.prop}: ${d.written}`));
}

function isReadableColor(value: string): boolean {
    const rgba = parseColor(value.trim().toLowerCase());
    return rgba != null && contrastWithWhite(...blend(rgba, [255, 255, 255])) >= MIN_TEXT_CONTRAST;
}

// Returns red, green and blue (0-255) and alpha (0-1), or null.
function parseColor(value: string, moreNames: Record<string, string> = {}): [number, number, number, number] | null {
    const color = hasKey(TEXT_COLOR_NAMES, value) ? TEXT_COLOR_NAMES[value]
        : hasKey(moreNames, value) ? moreNames[value] : value;
    const hex = color.match(/^#([0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/);
    if (hex) {
        const digits = hex[1].length <= 4 ? hex[1].replace(/./g, digit => digit + digit) : hex[1];
        const byte = (at: number) => parseInt(digits.slice(at, at + 2), 16);
        return [byte(0), byte(2), byte(4), digits.length === 8 ? byte(6) / 255 : 1];
    }
    const fn = color.match(/^(rgba?|hsla?)\(([^()]*)\)$/);
    if (!fn) return null;
    const parts = fn[2].trim().split(/\s*[,/]\s*|\s+/);
    if ((parts.length !== 3 && parts.length !== 4) || !parts.every(part => /^\d+(\.\d+)?(%|deg)?$/.test(part))) return null;
    const scaled = (part: string, max: number) => Math.min(max, part.endsWith('%') ? parseFloat(part) / 100 * max : parseFloat(part));
    const alpha = parts.length === 4 ? scaled(parts[3], 1) : 1;
    if (fn[1].startsWith('rgb')) {
        return [scaled(parts[0], 255), scaled(parts[1], 255), scaled(parts[2], 255), alpha];
    }
    if (!parts[1].endsWith('%') || !parts[2].endsWith('%')) return null;
    const [r, g, b] = hslToRgb(parseFloat(parts[0]), scaled(parts[1], 1), scaled(parts[2], 1));
    return [r, g, b, alpha];
}

function hslToRgb(hue: number, saturation: number, lightness: number): [number, number, number] {
    const a = saturation * Math.min(lightness, 1 - lightness);
    const channel = (n: number) => {
        const k = (n + hue / 30) % 12;
        return 255 * (lightness - a * Math.max(-1, Math.min(k - 3, 9 - k, 1)));
    };
    return [channel(0), channel(8), channel(4)];
}

// WCAG contrast ratio between an sRGB colour and white.
function contrastWithWhite(r: number, g: number, b: number): number {
    return 1.05 / (luminance(r, g, b) + 0.05);
}

// The relative luminance of an sRGB colour (WCAG).
function luminance(r: number, g: number, b: number): number {
    const linear = (channel: number) => {
        const c = channel / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}

function isResizedWidth(value: string): boolean {
    return RESIZED_WIDTH_PATTERN.test(value) && parseFloat(value) <= 100;
}

function isAttrAllowed(
    name: string,
    perTagAttrs: string[] | null,
    isMathML: boolean
): boolean {
    const lower = name.toLowerCase();
    if (isBlockedAttrName(name)) return false;
    if (GLOBAL_ATTRS.has(lower)) return true;
    if (isMathML) return MATHML_ATTRS.has(lower);
    if (perTagAttrs && perTagAttrs.includes(lower)) return true;
    return false;
}

function validateImgSrc(el: Element, opts: ResolvedOptions): void {
    const src = el.getAttribute('src');
    if (src == null) return;
    if (!isUrlAllowed(src, opts)) {
        el.removeAttribute('src');
    }
}

function isUrlAllowed(url: string, opts: ResolvedOptions): boolean {
    const trimmed = normalizeUrl(url);
    if (trimmed.startsWith('//')) return false;
    // Relative URL — anything that doesn't have a scheme.
    if (!/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return true;
    const lower = trimmed.toLowerCase();
    if (lower.startsWith('http://') || lower.startsWith('https://')) return true;
    if (lower.startsWith('data:')) {
        if (!opts.allowImageDataUrls) return false;
        const m = lower.match(/^data:([^;,]+)[;,]/);
        const mime = m ? m[1] : '';
        return opts.allowedImageMimeTypes.includes(mime);
    }
    return false;
}

function unwrap(el: Element): void {
    const parent = el.parentNode;
    if (!parent) return;
    while (el.firstChild) {
        parent.insertBefore(el.firstChild, el);
    }
    parent.removeChild(el);
}

// Elements without children that still show something.
const VOID_CONTENT_TAGS = new Set(['br', 'hr', 'img']);

// CKEditor turns div and similar wrappers into paragraphs. Unwrapping them
// must keep that line break, or "<div>a</div><div>b</div>" becomes "ab".
// The dir attribute of the block moves to its content, so mixed right-to-left
// text and numbers keep their order.
function unwrapBlock(el: Element, opts: ResolvedOptions): void {
    const dir = textDirection(el);
    if (isFlowContext(el, opts)) {
        wrapInlineRuns(el);
        if (dir) setBlockDirection(el, dir);
    } else if (hasContent(el)) {
        addLineBreaks(el);
        if (dir) wrapInDirectionSpan(el, dir);
    }
    unwrap(el);
}

function textDirection(el: Element): string | null {
    const dir = (el.getAttribute('dir') ?? '').trim().toLowerCase();
    return TEXT_DIRECTIONS.has(dir) ? dir : null;
}

// Blocks with their own dir keep it.
function setBlockDirection(el: Element, dir: string): void {
    for (const child of Array.from(el.children)) {
        if (BLOCK_TAGS.has(child.tagName.toLowerCase()) && !child.hasAttribute('dir')) {
            child.setAttribute('dir', dir);
        }
    }
}

function wrapInDirectionSpan(el: Element, dir: string): void {
    const span = el.ownerDocument.createElement('span');
    span.setAttribute('dir', dir);
    moveChildren(el, span);
    el.appendChild(span);
}

// True where the unwrapped content can become paragraphs. Parent wrappers are
// skipped, because they are unwrapped as well.
function isFlowContext(el: Element, opts: ResolvedOptions): boolean {
    let parent = el.parentElement;
    while (parent && UNWRAP_BLOCK_TAGS.has(parent.tagName.toLowerCase())
        && !keepsFigure(parent, opts) && !keepsCaption(parent, opts)) {
        parent = parent.parentElement;
    }
    if (!parent) return true;
    const tag = parent.tagName.toLowerCase();
    return tag === 'body' || FLOW_CONTAINER_TAGS.has(tag);
}

// Put each run of inline content between blocks into its own <p>.
function wrapInlineRuns(el: Element): void {
    let run: Node[] = [];
    const flush = () => {
        if (run.some(hasContent)) {
            const p = el.ownerDocument.createElement('p');
            el.insertBefore(p, run[0]);
            for (const node of run) p.appendChild(node);
        }
        run = [];
    };
    for (const node of Array.from(el.childNodes)) {
        if (node.nodeType === Node.ELEMENT_NODE && BLOCK_TAGS.has((node as Element).tagName.toLowerCase())) {
            flush();
        } else {
            run.push(node);
        }
    }
    flush();
}

// Where a paragraph cannot go (inside a heading or an inline element),
// separate the content from its neighbours with <br>.
function addLineBreaks(el: Element): void {
    const parent = el.parentNode;
    if (!parent) return;
    if (needsLineBreak(contentSibling(el, 'previousSibling'))) {
        parent.insertBefore(el.ownerDocument.createElement('br'), el);
    }
    if (needsLineBreak(contentSibling(el, 'nextSibling'))) {
        parent.insertBefore(el.ownerDocument.createElement('br'), el.nextSibling);
    }
}

function contentSibling(el: Element, direction: 'previousSibling' | 'nextSibling'): Node | null {
    let node = el[direction];
    while (node && !hasContent(node)) {
        node = node[direction];
    }
    return node;
}

function needsLineBreak(node: Node | null): boolean {
    return node != null && !(node.nodeType === Node.ELEMENT_NODE && (node as Element).tagName.toLowerCase() === 'br');
}

function hasContent(node: Node): boolean {
    if (node.nodeType === Node.TEXT_NODE) return /\S/.test(node.textContent ?? '');
    if (node.nodeType !== Node.ELEMENT_NODE) return false;
    if (VOID_CONTENT_TAGS.has((node as Element).tagName.toLowerCase())) return true;
    return Array.from(node.childNodes).some(hasContent);
}
